import { useCallback } from 'react';
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  TRACE_AGENT_NAMES,
  type TraceGroupCount,
} from '@terra/shared-contracts-trace';
import { terraProxyApiClient } from '../../clients/testerClient';
import type { LifecycleStateFilterDto } from '@terra/shared-dto-types';
import type {
  ExecutionsGroupCountsQuery,
  ExecutionsStatusCountsQuery,
} from '../../components/tabs/executions-table/executions-list.model';

const TRACE_TOTAL_COUNT_QUERY_KEY = 'trace-total-count';

export const useTracesTotalCount = ({
  applicationId,
  endpointIds,
  urlPatterns,
  lifecycleState,
  enabled = true,
}: {
  applicationId?: string;
  endpointIds?: string[];
  urlPatterns?: string[];
  lifecycleState?: LifecycleStateFilterDto;
  enabled?: boolean;
}) => {
  const queryClient = useQueryClient();
  const result = useQuery({
    queryKey: [
      TRACE_TOTAL_COUNT_QUERY_KEY,
      applicationId,
      endpointIds ?? [],
      urlPatterns ?? [],
      lifecycleState,
    ],
    queryFn: async () => {
      if (!applicationId) {
        return 0;
      }

      const response = await terraProxyApiClient.getTracesCountByApplication({
        applicationId,
        agentName: [...TRACE_AGENT_NAMES],
        endpointIds,
        urlPatterns,
        lifecycleState,
      });

      if (response.status !== 200) {
        throw new Error('Failed to fetch executions total count');
      }

      return response.body.total;
    },
    enabled: enabled && !!applicationId,
  });

  const invalidate = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: [TRACE_TOTAL_COUNT_QUERY_KEY],
      exact: false,
    });
  }, [queryClient]);

  return {
    totalCount: result.data ?? 0,
    isLoading: result.isLoading,
    refetch: result.refetch,
    invalidate,
  };
};

export const useTracesStatusCounts = ({
  query,
  enabled = true,
}: {
  query: ExecutionsStatusCountsQuery | null;
  enabled?: boolean;
}) => {
  const result = useQuery({
    queryKey: [TRACE_TOTAL_COUNT_QUERY_KEY, 'by-status', query],
    queryFn: async () => {
      if (!query) {
        return null;
      }

      const response = await terraProxyApiClient.getTracesCountByApplication({
        ...query,
        groupByStatus: true,
      });

      if (response.status !== 200) {
        throw new Error('Failed to fetch executions status counts');
      }

      return {
        total: response.body.total,
        byStatus: response.body.byStatus ?? null,
      };
    },
    enabled: enabled && query !== null,
  });

  return { statusCounts: result.data ?? null };
};

const NO_TRACE_GROUPS: TraceGroupCount[] = [];

export const useTracesGroupCounts = ({
  query,
  enabled = true,
}: {
  query: ExecutionsGroupCountsQuery | null;
  enabled?: boolean;
}) => {
  const result = useQuery({
    queryKey: [TRACE_TOTAL_COUNT_QUERY_KEY, 'by-group', query] as const,
    queryFn: async () => {
      if (!query) {
        return NO_TRACE_GROUPS;
      }

      const response =
        await terraProxyApiClient.getTracesCountByApplication(query);

      if (response.status !== 200) {
        throw new Error('Failed to fetch executions group counts');
      }

      return response.body.groups ?? NO_TRACE_GROUPS;
    },
    enabled: enabled && query !== null,
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[2]?.groupBy === query?.groupBy
        ? keepPreviousData(previousData)
        : undefined,
  });

  return {
    groups: result.data ?? NO_TRACE_GROUPS,
    hasGroups: result.isSuccess,
  };
};
